import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/InvoicesService';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export const useInvoices = (context: 'customer' | 'reports' = 'customer') => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    const { id: customerId } = useParams();
    
    const sort = { column: 'created', direction: 'desc' as const };
    const filter = (context === 'customer' && customerId) ? { customerId } : undefined;
    
    // INFINITE QUERY
    const invoicesQuery = useInfiniteQuery({
        queryKey: ['invoices', keyword, limit, sort.column, sort.direction, customerId],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, filter, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
        }
    });

    const fetchRecordsAction = () => {
        if (invoicesQuery.hasNextPage) {
            invoicesQuery.fetchNextPage();
        }
    };

    const reloadRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = invoicesQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: invoicesQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: invoicesQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reloadRecords,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};